import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import "./App.css";
import { Route, Routes, useNavigate } from "react-router-dom";
import Loader from "./components/loader/Loader";
import { useDispatch, useSelector } from "react-redux";
import app from "./services/feathers";
import { Button, Form, Input, message, Modal } from "antd";
import socket from "./services/socket";
import { GlobalProvider } from "./context/context";
import { URL_BASE_API } from "./constants";
import { setAuthData } from "./redux/slice/authSlice";
import { LockOutlined, UserOutlined } from "@ant-design/icons";

const AuthRedirect = lazy(() => import("./pages/redirect/AuthRedirect"));
const Home = lazy(() => import("./pages/home/Home"));
const Signin = lazy(() => import("./pages/authentication/signin/Signin"));
const Signup = lazy(() => import("./pages/authentication/signup/Signup"));
const SignupAgents = lazy(() =>
  import("./pages/authentication/signup/SignupAgents")
);
const ChangePasswordView = lazy(() =>
  import("./pages/authentication/change-password/ChangePasswordView")
);
const ResetPassswordView = lazy(() =>
  import("./pages/authentication/change-password/ResetPassswordView")
);
const NotFound = lazy(() => import("./pages/not-found/NotFound"));
const DashboardLayout = lazy(() => import("./layouts/DashboardLayout"));
const TransactionMortgage = lazy(() =>
  import("./pages/transaction-morgage/TransactionMortgage")
);
const TermsAndConditions = lazy(() =>
  import("./pages/terms-and-conditions/TermsAndConditions")
);
const PrivacityPolicy = lazy(() =>
  import("./pages/privacy-police/PrivacityPolice")
);

const App = () => {
  const workerRef = useRef(null);

  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  let user = JSON.parse(window.localStorage.getItem("user"));
  let accessToken = localStorage.getItem("feathers-jwt");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isModalVisible, setModalVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null); // en ms
  const [dismissedModal, setDismissedModal] = useState(false);
  const [viewExpiredToken, setViewExpiredToken] = useState("expired-token");
  const [submitting, setSubmitting] = useState(false);
  const connect = ({ strategy = "jwt", accessToken, ...rest }) => {
    return socket.authenticate({
      strategy,
      accessToken,
      ...rest,
    });
  };
  function decodeJwt(token) {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  }
  const onFinish = async (values) => {
    try {
      setSubmitting(true);
      const params = {
        strategy: "local",
        type_auth: "re_authenticate",
        email: auth?.user?.email, // ✔ más seguro
        password: values.password,
      };
      const res = await fetch(`${URL_BASE_API}/authentication`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Error renewing session");
      }

      const data = await res.json();
      const accessToken = data.accessToken;

      // Guardar token
      localStorage.setItem("feathers-jwt", accessToken);
      dispatch(setAuthData({ accessToken, user }));

      // Reiniciar Worker
      const payload = decodeJwt(accessToken);
      const newExpMs = payload.exp * 1000;

      workerRef.current?.terminate();
      workerRef.current = new Worker("/tokenWorker.js");
      workerRef.current.postMessage({ expTime: newExpMs });

      // Reset estados
      setModalVisible(false);
      setViewExpiredToken("expired-token");
      setDismissedModal(false);

      message.success("Session successfully renewed 🟢");
    } catch (err) {
      message.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  useEffect(() => {
    const token = localStorage.getItem("feathers-jwt");
    if (!token) {
      workerRef.current?.terminate();
      workerRef.current = null;
      setModalVisible(false);
      return;
    }
    const payload = decodeJwt(token);
    const expMs = payload.exp * 1000;
    // Esperar a que React cargue por completo (evita error en primera carga)
    requestAnimationFrame(() => {
      workerRef.current?.terminate();
      // 🔥 COMANDO CORRECTO
      workerRef.current = new Worker("/tokenWorker.js");
      workerRef.current.postMessage({ expTime: expMs });
      workerRef.current.onmessage = (e) => {
        const left = e.data;
        setTimeLeft(left);
        if (left <= 5 * 60 * 1000 && left > 0) {
          if (window.location.pathname !== "/signin") {
            setModalVisible(true);
          }
        }
        if (left <= 0) {
          workerRef.current?.terminate();
          workerRef.current = null;
          setModalVisible(false);
          localStorage.removeItem("feathers-jwt");
          localStorage.removeItem("user");
          setIsAuthenticated(false);
          navigate("/signin");
          window.location.reload();
        }
      };
    });
    return () => workerRef.current?.terminate();
  }, [accessToken]);

  useEffect(() => {
    if (!isModalVisible) return;
    const countdown = setInterval(() => {
      setTimeLeft((prev) => prev - 1000);
    }, 1000);
    return () => clearInterval(countdown);
  }, [isModalVisible]);
  useEffect(() => {
    // Función para manejar la reautenticación
    const reauthenticate = async () => {
      try {
        if (accessToken) {
          // Autentica automáticamente usando el token almacenado
          await app.authenticate({
            strategy: "jwt", // Usualmente es "jwt" para tokens de acceso
            accessToken: accessToken, // El token almacenado
          });
          if (!user) {
            localStorage.removeItem("feathers-jwt");
            localStorage.removeItem("trestle-token");
            localStorage.removeItem("notifications");
          }
          // Si la autenticación es exitosa, actualiza el estado solo si es necesario
          if (!isAuthenticated) {
            setIsAuthenticated(true);
            connect({
              accessToken,
            });
          }
        } else {
          localStorage.removeItem("user");

          // Si no hay token, muestra un mensaje o redirige
          // navigate("/signin");
        }
      } catch (error) {
        // message.error(error.message) //esto maneja el error de token vencido ;
        if (error.name === "NotAuthenticated") {
          // message.error("Your session has expired. Please log in again.");
          if (isAuthenticated) {
            setIsAuthenticated(false);
          }
          navigate("/signin");
        }
      }
    };
    reauthenticate();
  }, [accessToken, isAuthenticated, navigate]); // Asegúrate de que isAuthenticated y navigate sean necesarios aquí
  useEffect(() => {
    if (auth?.user?.theme) {
      document.documentElement.setAttribute("data-theme", auth.user.theme);
    }
  }, [auth.user]);
  return (
    <Suspense fallback={<Loader />}>
      <GlobalProvider>
        <AuthRedirect />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route
            path="/transaction-mortgage-purchase"
            element={<TransactionMortgage />}
          />
          <Route path="/signin" element={<Signin />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/signup-agents" element={<SignupAgents />} />
          <Route path="/reset-password" element={<ResetPassswordView />} />
          <Route
            path="/change-password/:token"
            element={<ChangePasswordView />}
          />
          <Route
            path="/terms-and-conditions"
            element={<TermsAndConditions />}
          />
          <Route path="/privacy-policy" element={<PrivacityPolicy />} />
          <Route path="/dashboard/*" element={<DashboardLayout />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </GlobalProvider>
      <Modal
        // title="⚠️Session Expiration Warning"
        // open={true}
        open={isModalVisible}
        onCancel={() => {
          setDismissedModal(true);
          setModalVisible(false);
        }}
        closable={false}
        // width={"38%"}
        footer={null}
      >
        {viewExpiredToken === "expired-token" ? (
          <div>
            <div className="title-modal-app">
              <span>⚠️</span>
              <span>Your session will expire in:</span>
            </div>
            <div className="counter-expire">
              <h2
                style={{
                  fontSize: "28px",
                  fontWeight: "bold",
                  textAlign: "center",
                  margin: 0,
                }}
              >
                {Math.max(0, Math.floor(timeLeft / 1000 / 60))}:
                {("0" + Math.max(0, Math.floor((timeLeft / 1000) % 60))).slice(
                  -2
                )}
              </h2>
            </div>
            <div className="footer-action-app">
              <p>Please save your work or renew your session.</p>
            </div>
            <div className="action-botton-app">
              <Button
                type="primary"
                onClick={() => setViewExpiredToken("form")}
              >
                Renew Session
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div className="title-modal-app">
              <span>
                Enter your password to
                <br /> continue the session
              </span>
            </div>
            <div className="form-token-app">
              <Form
                name="login"
                initialValues={{ remember: true }}
                style={{ maxWidth: 380 }}
                onFinish={onFinish}
              >
                <Form.Item
                  name="password"
                  rules={[
                    { required: true, message: "Please input your Password!" },
                    {
                      min: 6,
                      message: "Password must be at least 6 characters!",
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Password"
                    autoComplete="current-password"
                  />
                </Form.Item>
                <Form.Item>
                  <Button
                    block
                    type="primary"
                    className="btn-app-token"
                    htmlType="submit"
                    loading={submitting}
                  >
                    Log in
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </div>
        )}
      </Modal>
    </Suspense>
  );
};

export default App;
